/*
 * Product Add-ons Custom Element
 * Managed BMSM pricing adjustments, plus cart sync and update-cart behavior
 */

if (typeof ProductAddons === "undefined") {
  class ProductAddons extends HTMLElement {
    static syncListenerBound = false;

    constructor() {
      super();
      this.onProductAddOnClick = this.onProductAddOnClick.bind(this);
    }

    connectedCallback() {
      this.addOns = this.querySelectorAll('.product-addon');

      // BMSM tier percents: discount per tier index, from data-bmsm-tier-percents.
      this.tierPercents = (this.dataset.bmsmTierPercents || '0')
        .split(',')
        .map((value) => parseFloat(value) || 0);

      // Single delegated click handler on the host
      this.addEventListener('click', this.onProductAddOnClick);

      // Keyboard support for the checkbox-role toggle
      this.addOns.forEach((addOn) => {
        const toggle = addOn.querySelector('.add-bundle');
        if (toggle) {
          toggle.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
              event.preventDefault();
              toggle.click();
            }
          });
        }
      });

      ProductAddons.bindGlobalSyncListener();

      // Initial render: BMSM prices, progressive reveal, and hide the buy-box total if an add-on is active.
      this.refreshAddonDisplays();
      this.applyProgressiveReveal();
      this.updateAtcPriceVisibility();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onProductAddOnClick);
    }

    static bindGlobalSyncListener() {
      if (ProductAddons.syncListenerBound) {
        return;
      }

      ProductAddons.syncListenerBound = true;
      document.addEventListener('product-addons:sync', ProductAddons.handleGlobalSync);
    }

    static handleGlobalSync(event) {
      if (!event.detail?.cart) {
        return;
      }

      const { cart, addonCartStatusByVariantId } = event.detail;

      for (const host of document.querySelectorAll('product-addons')) {
        if (host.isConnected) {
          host.syncFromCart(cart, addonCartStatusByVariantId);
        }
      }
    }

    setAddonCartStatus(addOn, status) {
      const container = addOn.querySelector('.addon-headline-prefix');
      if (!container || !window.ProductShop?.updatePurchaseStatusOnElement) return;
      window.ProductShop.updatePurchaseStatusOnElement(container, status);
    }

    getAddonCartStatus(addOn, addonCartStatusByVariantId) {
      const variantKey = String(addOn.dataset.variantId || '');
      if (addonCartStatusByVariantId?.[variantKey]) {
        return addonCartStatusByVariantId[variantKey];
      }
      // Fallback before the first cart sync: in-cart vs not status from the server-rendered data attribute.
      return addOn.dataset.addonInCart === 'true' ? 'update' : 'allowed';
    }

    getActiveAddOns() {
      return [...this.addOns].filter((addOn) => addOn.classList.contains('active'));
    }

    hasActiveAddon() {
      return this.getActiveAddOns().length > 0;
    }

    updateAtcPriceVisibility() {
      const form = this.closest('.c-product-purchase__form');
      if (!form) {
        return;
      }

      // Hide the buy box price when an add-on is active: the PDP base price does not reflect BMSM,
      // so any total shown alongside an add-on would be inaccurate
      const hidePrice = this.hasActiveAddon();
      form.querySelectorAll('[data-price-wrapper]').forEach((wrapper) => {
        wrapper.hidden = hidePrice;
      });
    }

    // --- BMSM pricing ---
    getAddonSubPrice(addOn, purchaseType) {
      if (purchaseType === 'onetime') {
        return parseFloat(addOn.dataset.onetimePrice);
      }
      if (purchaseType === 'quarterly') {
        return parseFloat(addOn.dataset.subPriceQuarterly);
      }
      return parseFloat(addOn.dataset.subPriceMonthly);
    }

    getAddonDisplayPrice(addOn, tierIndex, purchaseType) {
      if (purchaseType === 'onetime') {
        return this.getAddonSubPrice(addOn, purchaseType);
      }
      const base = this.getAddonSubPrice(addOn, purchaseType);
      const tierPercent = this.tierPercents[tierIndex] ?? 0;
      return (base * (100 - tierPercent)) / 100;
    }

    getAddonTierIndex(addOn) {
      // Find the discount tier the add-on should be part of.
      // Every active add-on shares the tier for the current total,
      // and an inactive add-on previews the tier it would trigger if added next.
      const activeCount = this.getActiveAddOns().length;
      const index = addOn.classList.contains('active') ? activeCount - 1 : activeCount;
      return Math.max(0, Math.min(index, this.tierPercents.length - 1));
    }

    formatPrice(amount) {
      return `$${Shopify.formatMoney(amount * 100, '{{amount}}').replace('.00', '')}`;
    }

    getAddonSavingsPercent(addOn, displayPrice) {
      const comparePrice = parseFloat(addOn.dataset.onetimePrice);
      if (!comparePrice || comparePrice <= 0) {
        return 0;
      }
      return Math.floor(((comparePrice - displayPrice) / comparePrice) * 100);
    }

    updateAddonDisplay(addOn, purchaseType) {
      const tierIndex = this.getAddonTierIndex(addOn);
      const subPrice = this.getAddonSubPrice(addOn, purchaseType);
      const displayPrice = this.getAddonDisplayPrice(addOn, tierIndex, purchaseType);

      addOn.querySelector('.addon-price').innerText = this.formatPrice(displayPrice);

      const savingsEl = addOn.querySelector('.addon-savings');
      const strikethroughEl = addOn.querySelector(
        '.bundle-creator__bundle-offers__bundle-offer__price__original .injected'
      );

      if (purchaseType === 'onetime') {
        $(addOn).find('.onetime-hide').hide();
        return;
      }

      $(addOn).find('.onetime-hide').show();

      if (strikethroughEl) {
        strikethroughEl.textContent = this.formatPrice(subPrice);
      }

      if (savingsEl) {
        savingsEl.innerText = `${this.getAddonSavingsPercent(addOn, displayPrice)}`;
      }
    }

    /**
     * Refresh every add-on's own price / savings display for the currently-checked plan, applying
     * BMSM tier discounts.  Runs on every cart-driven sync (dispatched on any plan change,
     * cart change, or add-on toggle), so add-on prices stay aligned with the selected plan and the
     * active-count tier in both the legacy and redesign buy boxes.
     *
     * The buy-box button total is intentionally NOT written here: it is hidden while an add-on is
     * active (see updateAtcPriceVisibility), because the PDP base price does not reflect BMSM.
     */
    refreshAddonDisplays(purchaseForm = this.closest('.c-product-purchase__form')) {
      const selectedPurchase = purchaseForm?.querySelector('.c-product-purchase__radio:checked');
      if (!selectedPurchase) {
        return;
      }
      const purchaseType = selectedPurchase.getAttribute('data-purchase-type');
      this.addOns.forEach((addOn) => this.updateAddonDisplay(addOn, purchaseType));
    }

    /**
     * Progressive reveal: reveal every add-on up to (and including)
     * the one after the highest-index "engaged" add-on (active or already in cart).
     * Reveal-only: never re-hides an add-on that is already shown.
     */
    applyProgressiveReveal() {
      const ordered = [...this.addOns].sort(
        (a, b) =>
          (parseInt(a.dataset.addonIndex, 10) || 0) - (parseInt(b.dataset.addonIndex, 10) || 0)
      );
      // Highest position that is engaged (active or in cart); -1 when none are.
      let lastEngaged = -1;
      ordered.forEach((addOn, i) => {
        if (addOn.classList.contains('active') || addOn.dataset.addonInCart === 'true') {
          lastEngaged = i;
        }
      });
      // Always show the first add-on (index 0), plus everything through the next one after the last engaged.
      const revealThrough = Math.max(0, lastEngaged + 1);
      ordered.forEach((addOn, i) => {
        if (i <= revealThrough) {
          addOn.classList.remove('hidden');
        }
      });
    }

    /** Update checkmark / in-cart state from cart payload, then refresh prices + reveal */
    syncFromCart(cart, addonCartStatusByVariantId) {
      this._addonCartStatusByVariantId = addonCartStatusByVariantId;

      const itemsInCart = (cart?.items || [])
        .filter((item) => !item.properties?._is_free_sample && !item.properties?._is_auto_GWP)
        .map((item) => String(item.variant_id));

      this.addOns.forEach((addOn) => {
        const monthlyId = String(addOn.dataset.variantId || '');
        const quarterlyId = String(addOn.dataset.variantIdQuarterly || '');
        const inCart =
          itemsInCart.includes(monthlyId) || itemsInCart.includes(quarterlyId);
        const wasInCart = addOn.dataset.addonInCart === 'true';

        if (inCart) {
          addOn.dataset.addonInCart = 'true';
          // Preserve a customer's manual de-selection until the cart line is actually removed,
          // so a sync (e.g. plan change) doesn't re-check an add-on they just unchecked.
          if (addOn.dataset.addonDeselected === 'true') {
            addOn.classList.remove('active');
          } else {
            addOn.classList.add('active');
          }
        } else {
          if (wasInCart) {
            addOn.classList.remove('active');
          }
          addOn.dataset.addonInCart = 'false';
          delete addOn.dataset.addonDeselected;
        }

        const deselected = inCart && addOn.dataset.addonDeselected === 'true';
        const status = deselected
          ? 'allowed'
          : this.getAddonCartStatus(addOn, addonCartStatusByVariantId);
        this.setAddonCartStatus(addOn, status);
      });

      // Reveal add-ons progressively from what is now engaged (active or in cart).
      this.applyProgressiveReveal();

      // Refresh BMSM per-add-on prices for the selected plan, and hide the buy-box total if active.
      this.refreshAddonDisplays();
      this.updateAtcPriceVisibility();
    }

    onProductAddOnClick(event) {
      if (event.target.matches('a[href]')) {
        return;
      }

      const addOn = event.target.closest('.product-addon');
      if (!addOn || !this.contains(addOn)) {
        return;
      }

      event.preventDefault();

      const $addOn = $(addOn);
      $addOn.toggleClass('active');
      const isActive = $addOn.hasClass('active');
      const inCart = addOn.dataset.addonInCart === 'true';

      // a11y: reflect the toggle state on the checkbox-role control
      const toggle = addOn.querySelector('.add-bundle');
      if (toggle) {
        toggle.setAttribute('aria-checked', String(isActive));
      }

      // Track de-selection of an in-cart add-on so it is removed on the next Add/Update Cart click
      // and not re-activated by a cart sync (e.g. plan change) before the customer clicks
      if (inCart && !isActive) {
        addOn.dataset.addonDeselected = 'true';
      } else {
        delete addOn.dataset.addonDeselected;
      }

      // Reveal the next add-on in index order once this one is engaged
      this.applyProgressiveReveal();

      // The active count shifts the BMSM tier index for every add-on, so re-price them all
      this.refreshAddonDisplays();

      const status = isActive
        ? this.getAddonCartStatus(addOn, this._addonCartStatusByVariantId)
        : 'allowed';
      this.setAddonCartStatus(addOn, status);

      this.updateAtcPriceVisibility();

      // Selecting / de-selecting an add-on can change the main CTA (e.g. denied -> update).
      // Re-resolve the buy-box label from the cached cart (no fetch)
      window.ProductShop?.reapplyBuyBoxPurchaseStateFromCache?.();
    }
  }
  customElements.get('product-addons') || customElements.define('product-addons', ProductAddons);
}
